import { LogLevel, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { environment } from '@environments/environment';

/**
 * The Subscription Portal accepts two sign-in systems side by side, like the SP API does:
 * - `onelogin`: OneLogin (OpenIddict). Thin RS256 access token; SP identity comes from `auth/context`.
 * - `identity`: the legacy Capium Identity server, configured exactly as before OneLogin (master).
 * Each environment file lists both under `auth`; one flag switches either off.
 */
export type AuthProviderId = 'onelogin' | 'identity';

export const AUTH_PROVIDER_IDS: readonly AuthProviderId[] = ['onelogin', 'identity'];

export interface AuthProviderSettings {
  enabled: boolean;
  /** OIDC authority (issuer). */
  authority: string;
  clientId: string;
  /** Where "home" and module fallback links go (OneLogin hub / Identity site). */
  homeUrl: string;
  /** Where a signed-out user is sent (post-logout and unauthorized). */
  loginUrl: string;
  /** Account / profile page for the header menu. */
  profileUrl: string;
  /** SP API bases this system's token is sent to. */
  apiVersionUrl: string;
  apiVersion2Url: string;
}

export interface AuthSettings {
  defaultProvider: AuthProviderId;
  onelogin: AuthProviderSettings;
  identity: AuthProviderSettings;
}

/** `?idp=` on any entry link picks the sign-in system (e.g. the OneLogin hub adds `?idp=onelogin`). */
export const AUTH_PROVIDER_HINT_PARAM = 'idp';

/**
 * The system this browser TAB uses (sessionStorage, like the library's tokens). The hub opens the
 * portal in a new tab with `?idp=onelogin`; that tab stays on OneLogin through sign-in and reloads,
 * while any other tab or link without a hint gets the environment default (Identity) untouched.
 * Kept across `sessionStorage.clear()` by the guard.
 */
export const AUTH_PROVIDER_STORAGE_KEY = 'spAuthProvider';

export function authSettings(): AuthSettings {
  return (environment as unknown as { auth: AuthSettings }).auth;
}

export function providerSettings(id: AuthProviderId): AuthProviderSettings {
  return authSettings()[id];
}

export function enabledProviders(): AuthProviderId[] {
  return AUTH_PROVIDER_IDS.filter((id) => !!authSettings()[id]?.enabled);
}

export function isProviderId(value: unknown): value is AuthProviderId {
  return value === 'onelogin' || value === 'identity';
}

export function isEnabledProvider(value: unknown): value is AuthProviderId {
  return isProviderId(value) && enabledProviders().includes(value);
}

/** The environment default, or the first enabled system when the default is switched off. */
export function defaultProvider(): AuthProviderId {
  const preferred = authSettings().defaultProvider;
  return isEnabledProvider(preferred) ? preferred : enabledProviders()[0];
}

/**
 * Picks the sign-in system for a request that is not an OIDC callback: the link hint, then the
 * system this tab already uses, then a system with a session in this tab (tokens are per tab),
 * then the environment default. A link without a hint in a new tab always gets the default.
 */
export function chooseProvider(
  hint: unknown,
  remembered: unknown,
  authenticated: AuthProviderId[]
): AuthProviderId {
  if (isEnabledProvider(hint)) {
    return hint;
  }
  if (isEnabledProvider(remembered)) {
    return remembered;
  }
  const usable = authenticated.filter((id) => isEnabledProvider(id));
  if (usable.length > 0) {
    return usable[0];
  }
  return defaultProvider();
}

/** One OIDC configuration per enabled system; `configId` is the provider id. */
export function buildOidcConfigs(origin: string): OpenIdConfiguration[] {
  const configs: OpenIdConfiguration[] = [];
  const oneLogin = authSettings().onelogin;
  if (oneLogin?.enabled) {
    configs.push({
      configId: 'onelogin',
      authority: oneLogin.authority,
      // Trailing slash required: OpenIddict stores root redirect URIs as http://host:port/
      // (System.Uri normalizes); window.location.origin has no slash → ID2043.
      redirectUrl: `${origin}/`,
      postLogoutRedirectUri: oneLogin.loginUrl,
      clientId: oneLogin.clientId,
      scope: 'openid profile email offline_access',
      responseType: 'code',
      silentRenew: true,
      silentRenewUrl: `${origin}/silent-renew.html`,
      logLevel: environment.production ? LogLevel.Warn : LogLevel.Debug,
      historyCleanupOff: false,
      autoCleanStateAfterAuthentication: true,
      postLoginRoute: '/home/products',
      unauthorizedRoute: oneLogin.loginUrl,
      autoUserInfo: false, // auth.guard reads OneLogin access_token claims
      useRefreshToken: true,
    });
  }
  const identity = authSettings().identity;
  if (identity?.enabled) {
    // Unchanged from the Identity-only build (master): same client, scope and redirect.
    configs.push({
      configId: 'identity',
      authority: identity.authority,
      redirectUrl: origin,
      postLogoutRedirectUri: identity.loginUrl,
      clientId: identity.clientId,
      scope: 'capium_myadmin_api openid',
      responseType: 'code',
      silentRenew: false,
      silentRenewUrl: `${origin}/silent-renew.html`,
      logLevel: environment.production ? LogLevel.Warn : LogLevel.Debug,
      historyCleanupOff: true,
      autoCleanStateAfterAuthentication: true,
      postLoginRoute: '/home/products',
      unauthorizedRoute: identity.loginUrl,
      autoUserInfo: false,
    });
  }
  return configs;
}

function decodeSegment(segment: string): Record<string, unknown> | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const pad = 4 - (base64.length % 4);
    const padded = pad === 4 ? base64 : base64 + '='.repeat(pad);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const value = JSON.parse(json);
    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
}

/** Header and payload of a signed JWT (3 parts); null for anything else (JWE, opaque, garbage). Never verifies. */
export function readJwt(
  token: string | null | undefined
): { header: Record<string, unknown>; payload: Record<string, unknown> } | null {
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  const header = decodeSegment(parts[0]);
  const payload = decodeSegment(parts[1]);
  return header && payload ? { header, payload } : null;
}

function sameIssuer(a: unknown, b: string | undefined): boolean {
  if (typeof a !== 'string' || !b) {
    return false;
  }
  const norm = (s: string) => s.trim().replace(/\/+$/, '').toLowerCase();
  return norm(a) === norm(b);
}

/**
 * OneLogin = JWS RS256 (3 parts) that is not an Identity token (no `tenantName`, issuer is not the
 * Identity authority). The library has already validated the issuer of the token it stored for the
 * `onelogin` configuration; this only keeps a legacy token from being sent where OneLogin is expected.
 */
export function isOneLoginAccessToken(token: string | null | undefined): boolean {
  const jwt = readJwt(token);
  if (!jwt || jwt.header['enc'] || jwt.header['alg'] !== 'RS256') {
    return false;
  }
  return (
    !jwt.payload['tenantName'] &&
    !sameIssuer(jwt.payload['iss'], authSettings().identity?.authority)
  );
}

/** Identity access token as the Identity-only build accepted it: a readable JWT carrying `tenantName`. */
export function isIdentityAccessToken(token: string | null | undefined): boolean {
  const jwt = readJwt(token);
  return !!jwt && !!jwt.payload['tenantName'];
}

export function isAccessTokenFor(
  provider: AuthProviderId,
  token: string | null | undefined
): boolean {
  return provider === 'onelogin'
    ? isOneLoginAccessToken(token)
    : isIdentityAccessToken(token);
}

function startsWithBase(url: string, base: string | undefined): boolean {
  return !!base && url.toLowerCase().startsWith(base.toLowerCase());
}

/**
 * Services build SP URLs from `environment.apiVersionUrl` / `apiVersion2Url`. When the active system
 * uses another SP host (dev: OneLogin through APIM, Identity direct to storeapi, because APIM only
 * accepts OneLogin tokens) the prefix is swapped here. Other URLs are returned unchanged.
 */
export function rewriteApiUrl(url: string, provider: AuthProviderId): string {
  const target = providerSettings(provider);
  if (!target) {
    return url;
  }
  const env = environment as unknown as Record<string, string | undefined>;
  const pairs: Array<[string | undefined, string]> = [
    [env['apiVersion2Url'], target.apiVersion2Url],
    [env['apiVersion2UrlDirect'], target.apiVersion2Url],
    [env['apiVersionUrl'], target.apiVersionUrl],
    [env['apiVersionUrlDirect'], target.apiVersionUrl],
  ];
  for (const [from, to] of pairs) {
    if (from && to && startsWithBase(url, from)) {
      return to + url.substring(from.length);
    }
  }
  return url;
}

/** True when a bearer for `provider` may be attached: its SP API bases and the communication API only. */
export function acceptsBearer(url: string, provider: AuthProviderId): boolean {
  const target = providerSettings(provider);
  const common = (environment as unknown as { commonapiVersionUrl?: string }).commonapiVersionUrl;
  return (
    startsWithBase(url, target?.apiVersionUrl) ||
    startsWithBase(url, target?.apiVersion2Url) ||
    startsWithBase(url, common)
  );
}

/** True for requests to any configured OIDC authority (they carry no API bearer). */
export function isAuthorityUrl(url: string): boolean {
  return AUTH_PROVIDER_IDS.some((id) => {
    const authority = authSettings()[id]?.authority;
    return !!authority && startsWithBase(url, authority.replace(/\/+$/, ''));
  });
}
